import { Link } from 'react-router-dom';
import { MessageCircle, Phone, Mail, MapPin, Truck, Shield, Headphones, ArrowRight, Leaf } from 'lucide-react';
import { FacebookIcon, InstagramIcon, YoutubeIcon, TiktokIcon } from '../ui/SocialIcons';

export function Footer() {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'Home', path: '/' },
    { label: 'Shop', path: '/shop' },
    { label: 'Cart', path: '/cart' },
    { label: 'Contact Us', path: '/contact' },
  ];

  const contactItems = [
    { icon: Phone, label: '0305-6367673', href: 'tel:03056367673' },
    { icon: MessageCircle, label: 'WhatsApp', href: 'https://wa.me/923056367673', isExternal: true },
    { icon: Mail, label: 'wellzoraherbalcare@gmail.com', href: 'mailto:wellzoraherbalcare@gmail.com' },
    { icon: MapPin, label: 'Lahore, Pakistan', href: null },
  ];

  const socialLinks = [
    { icon: FacebookIcon, url: 'https://www.facebook.com/share/198qJNqxMp/?mibextid=wwXIfr', label: 'Facebook' },
    { icon: InstagramIcon, url: 'https://www.instagram.com/wellzora_herbal_care?igsi=YmQxanF4dzZmOWd6&utm_source=qr', label: 'Instagram' },
    { icon: YoutubeIcon, url: '#', label: 'YouTube' },
    { icon: TiktokIcon, url: '#', label: 'TikTok' },
    { icon: MessageCircle, url: 'https://wa.me/923056367673', label: 'WhatsApp' },
  ];

  const trustItems = [
    { icon: Truck, title: 'Fast Delivery', desc: '3–5 working days Pakistan-wide' },
    { icon: Shield, title: 'DRAP Certified', desc: 'Quality assured & lab tested' },
    { icon: Headphones, title: '24/7 Support', desc: 'WhatsApp & phone support' },
  ];

  const policies = [
    { label: 'Privacy Policy', path: '/privacy-policy' },
    { label: 'Shipping Policy', path: '/shipping-policy' },
    { label: 'Terms & Disclaimer', path: '/terms' },
  ];

  return (
    <footer className="bg-gray-950 text-gray-400">
      {/* Gradient top border */}
      <div className="h-1 bg-gradient-to-r from-primary-dark via-primary-light to-accent" />

      {/* Trust strip */}
      <div className="border-b border-gray-800 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {trustItems.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-primary-light" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">{item.title}</p>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-5" aria-label="WellZora Herbal Care Home">
              <img
                src="/assets/Logo.jpg"
                alt="WellZora Herbal Care"
                className="h-12 w-auto rounded-xl"
              />
              <div>
                <p className="font-bold text-white text-base leading-tight">WellZora</p>
                <p className="text-primary-light text-xs font-medium leading-tight">Herbal Care</p>
              </div>
            </Link>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              Nature's power for better health. 100% natural herbal products, laboratory prepared and trusted by millions across Pakistan.
            </p>
            {/* Social links */}
            <div className="flex flex-wrap gap-2">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-xl bg-gray-800 flex items-center justify-center hover:bg-primary hover:text-white text-gray-500 transition-all duration-300"
                  aria-label={social.label}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="font-semibold text-white mb-5 text-sm uppercase tracking-wider">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-primary-light transition-colors duration-200 group"
                  >
                    <ArrowRight className="w-3.5 h-3.5 text-primary/50 group-hover:text-primary-light group-hover:translate-x-0.5 transition-all duration-200" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-white mb-5 text-sm uppercase tracking-wider">Contact</h3>
            <ul className="space-y-3">
              {contactItems.map((item, index) => (
                <li key={index}>
                  {item.href ? (
                    <a
                      href={item.href}
                      target={item.isExternal ? '_blank' : undefined}
                      rel={item.isExternal ? 'noopener noreferrer' : undefined}
                      className="flex items-start gap-2.5 text-sm text-gray-500 hover:text-primary-light transition-colors duration-200"
                    >
                      <item.icon className="w-4 h-4 text-primary/60 mt-0.5 flex-shrink-0" />
                      <span className="break-all">{item.label}</span>
                    </a>
                  ) : (
                    <span className="flex items-start gap-2.5 text-sm text-gray-500">
                      <item.icon className="w-4 h-4 text-primary/60 mt-0.5 flex-shrink-0" />
                      {item.label}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Hours & leaf */}
          <div>
            <h3 className="font-semibold text-white mb-5 text-sm uppercase tracking-wider">Business Hours</h3>
            <div className="space-y-2 text-sm text-gray-500 mb-6">
              <p>Monday – Saturday</p>
              <p className="text-white font-medium">10:00 AM – 8:00 PM</p>
              <p className="mt-3">Sunday</p>
              <p className="text-white font-medium">By Appointment</p>
            </div>
            <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-xl border border-primary/20">
              <Leaf className="w-5 h-5 text-primary-light flex-shrink-0" />
              <p className="text-xs text-gray-400">100% Natural & DRAP Approved Products</p>
            </div>
          </div>
        </div>
      </div>

      {/* Herbal Dietary Disclaimer (E-E-A-T Compliance) */}
      <div className="border-t border-gray-900 bg-gray-950/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-[11px] text-gray-500 text-center leading-relaxed max-w-4xl mx-auto">
            <span className="font-semibold text-gray-400">Herbal Supplement Disclaimer:</span> WellZora Herbal Care formulations are traditional, 100% natural herbal preparations. Information provided on this website is for informational and educational purposes only and is not intended to diagnose, treat, cure, or prevent any medical condition. Results may vary across individuals. Always consult a qualified physician or healthcare professional regarding any medical concerns.
          </p>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
            <p>© {currentYear} WellZora Herbal Care. All rights reserved.</p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              {policies.map((policy, i) => (
                <Link key={i} to={policy.path} className="hover:text-primary-light transition-colors duration-200">
                  {policy.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}